import WelcomeMessages from "@/domain/entities/dashboard/WelcomeMessages";
import { eventKeys } from "@/lib/eventKeys";
import { queryKeys } from "@/lib/queryKeys";
import welcomeMessagesMapper from "@/mappers/user/welcomeMessageMapper";
import {
    WelcomeMessages as SDKWelcomeMessages,
    useSdk,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import useGetEventBus from "./useGetEventBus";
import useFetchErrorNotifier from "./utils/useFetchErrorNotifier";

const useWelcomeMessages = (): WelcomeMessages | undefined => {
    const { mailManager } = useSdk();
    const queryClient = useQueryClient();
    const notifyFetchError = useFetchErrorNotifier();

    const fetchWelcomeMessages = async (): Promise<WelcomeMessages> => {
        try {
            const welcomeMessagesfromSDK = mailManager.getWelcomeMessages();

            if (welcomeMessagesfromSDK) {
                return welcomeMessagesMapper(welcomeMessagesfromSDK);
            }
            throw new Error("welcomeMessagesfromSDK is undefined");
        } catch (error) {
            console.error(error);
            notifyFetchError({
                queryKey: queryKeys.welcomeMessages(),
                titleTranslationKey: "errors.dashboardSummary",
            });
            throw error;
        }
    };

    const { data: welcomeMessagesInfos } = useQuery({
        queryKey: queryKeys.welcomeMessages(),
        queryFn: fetchWelcomeMessages,
        // [AB#1710 fix] Un event 'dashboard-welcome-messages-updated' émis avant le montage de ce hook
        // est perdu (mitt ne bufferise pas) et le SDK ne le ré-émet pas (dédup) → welcome figé (staleTime
        // global 1h). On relit donc l'état SDK à CHAQUE montage — le pull getWelcomeMessages() retourne
        // désormais le dernier payload émis (même source que l'event).
        refetchOnMount: "always",
    });

    const handleWelcomeMessagesUpdated = (
        welcomeMessages: SDKWelcomeMessages,
    ) => {
        const mappedWelcomeMessages = welcomeMessagesMapper(welcomeMessages);
        queryClient.setQueryData(
            queryKeys.welcomeMessages(),
            mappedWelcomeMessages,
        );
    };

    const { off: offWelcomeMessagesUpdated } =
        useGetEventBus<SDKWelcomeMessages>(
            eventKeys.welcomeMessages.updated,
            handleWelcomeMessagesUpdated,
        );

    useEffect(
        () => () => {
            offWelcomeMessagesUpdated(
                eventKeys.welcomeMessages.updated,
                handleWelcomeMessagesUpdated,
            );
        },
        [offWelcomeMessagesUpdated],
    );

    return welcomeMessagesInfos;
};

export default useWelcomeMessages;
